// 已知的 pulse 靜態資產 CDN host。widget 對外 asset base 僅允許這些 host——
// 定位與 api-origin.ts 相同：防誤設／部分入侵（D3=A：宿主可信，非防惡意宿主 sandbox escape）。
export const ALLOWED_ASSET_HOSTS: readonly string[] = [
  'assets.pulse.lab.tvbs-staging.app',
  'assets.pulse.lab.tvbs.app',
]

// 解析成功、https、host 為 allowlist 之一、不帶帳密（user@host），且形狀是 logoUrl 可以直接拼接的
// base（無結尾斜線、無 query、無 hash）才 true。帳密與三個結構性檢查（trailing slash / query / hash）
// 是 asset base 特有的：帶帳密的子資源請求瀏覽器不會送出，logo 會直接收起而且沒有 warn；api-origin.ts 的 isAllowedApiHost 沒有這些，因為 API base 是
// origin-only、後面永遠接 /v1/... 的 path，不是直接字串拼接。
// 兩個問題共用這支述詞：build 時「這個 baked 值可以出貨嗎」（build/assert-asset-base.ts）、
// runtime 時「這個 override 收不收」（decideAssetBase）。兩者是不同決策，只是判準相同。
export function isAllowedAssetBase(url: string): boolean {
  let parsed: URL
  try {
    parsed = new URL(url)
  }
  catch {
    return false
  }
  return parsed.protocol === 'https:'
    && ALLOWED_ASSET_HOSTS.includes(parsed.host)
    && !parsed.username
    && !parsed.password
    && !url.endsWith('/')
    && !parsed.search
    && !parsed.hash
}

/**
 * 一次 logo URL 要用哪個 asset base，以及為什麼。
 *
 * 形狀與 api-origin.ts 的 ApiBaseDecision 對稱，但兩者刻意各自宣告、不共用型別或 decide
 * 函式本體：asset base 的合法判準比 API host 多了帳密與三個結構性條件（trailing slash / query /
 * hash），硬抽一個通用「decide」會把判準參數化成一個 predicate function，讀者要跳進呼叫端
 * 才知道某個分支實際擋了什麼；兩份各自獨立的小檔案比一份帶抽象參數的通用政策引擎更容易讀懂
 * 「這裡到底允許什麼」。
 */
export type AssetBaseDecision
  = | { readonly outcome: 'baked', readonly base: string }
    | { readonly outcome: 'override', readonly base: string }
  // 被拒絕的 override 原樣帶出來，讓呼叫端說得出「忽略掉的是什麼」。型別是 unknown 而非 string：
  // 非字串的 override 也走這一臂。
    | { readonly outcome: 'rejected', readonly base: string, readonly override: unknown }

/**
 * asset base 政策的唯一所在地：baked 值、host override 與所在環境進來，決定這次 logo 請求用哪個 base。
 *
 * 收下 `baked` / `isDev` 而不自己讀 `import.meta.env`，是為了讓政策不必經過瀏覽器就驗得到——
 * 讀全域那一格留在 design-system/logos.ts 的 resolveAssetBase。
 *
 * override 收緊規則：DEV 接受任意字串（playground 的本機資產伺服器）；production 只認 allowlist。
 * override 來自宿主注入的 global，runtime 型別不可信（可能是物件／數字），故先擋非字串，
 * 確保 base 永遠是字串。falsy 的 override（未設定、空字串）等同沒設定，直接用 baked、不算被拒。
 */
export function decideAssetBase(input: { baked: string, override: unknown, isDev: boolean }): AssetBaseDecision {
  const { baked, override, isDev } = input
  if (!override)
    return { outcome: 'baked', base: baked }
  if (typeof override === 'string' && (isDev || isAllowedAssetBase(override)))
    return { outcome: 'override', base: override }
  return { outcome: 'rejected', base: baked, override }
}
